﻿import { bootstrapSheet } from '../../core/styles/bootstrap.js';
import { eumBaseSheet } from '../../core/styles/eum-base.js';
// @ts-ignore — handled by esbuild inline-css plugin
import componentSheet from './eum-facet-panel.css';
import { FacetData } from '../../eum-search-base.js';
import { renderFormat } from '../../core/utils/format-engine.js';
import { i18n } from '../../core/i18n/i18n-service.js';

/**
 * <eum-facet-panel> — renders facet groups with clickable values.
 * Facets are additive (multiple values can be selected across groups).
 * Selected facets appear as closable pills at the top.
 * Driven via attribute: data-facets (JSON string of FacetData[]).
 * Fires `eum-facet-change` with { facetKey, value }.
 * Fires `eum-facets-clear` to reset all selections.
 */
export class EumFacetPanel extends HTMLElement {
    private container!: HTMLElement;

    static get observedAttributes(): string[] {
        return ['data-facets'];
    }

    connectedCallback(): void {
        const shadow = this.attachShadow({ mode: 'open' });
        shadow.adoptedStyleSheets = [bootstrapSheet, eumBaseSheet, componentSheet];

        shadow.innerHTML = /*html*/ `<div class="facet-panel"></div>`;

        this.container = shadow.querySelector('.facet-panel')!;
        this.render();

        this.container.addEventListener('click', (e: Event) => {
            const target = e.target as HTMLElement;

            // Facet value click (toggle selection — additive)
            const valueEl = target.closest<HTMLElement>('[data-facet-key][data-facet-value]');
            if (valueEl) {
                this.dispatchEvent(
                    new CustomEvent('eum-facet-change', {
                        bubbles: true,
                        composed: true,
                        detail: {
                            facetKey: valueEl.dataset.facetKey,
                            value: valueEl.dataset.facetValue,
                        },
                    })
                );
                return;
            }

            // Clear all refiners
            if (target.closest('.btn-clear-facets')) {
                this.dispatchEvent(
                    new CustomEvent('eum-facets-clear', {
                        bubbles: true,
                        composed: true,
                    })
                );
            }
        });
    }

    attributeChangedCallback(): void {
        if (this.container) this.render();
    }

    private get facets(): FacetData[] {
        const raw = this.getAttribute('data-facets');
        if (!raw) return [];
        try {
            return JSON.parse(raw);
        } catch {
            return [];
        }
    }

    private render(): void {
        const facets = this.facets.filter((f) => f.values.length > 0);

        if (facets.length === 0) {
            this.container.innerHTML = '';
            return;
        }

        // Collect all selected values across all facet groups
        const selectedPills: { facetKey: string; facetLabel: string; rawValue: string; displayValue: string }[] = [];
        for (const facet of facets) {
            for (const v of facet.values) {
                if (v.selected) {
                    selectedPills.push({
                        facetKey: facet.key,
                        facetLabel: facet.label,
                        rawValue: String(v.value),
                        displayValue: this.renderFacetValue(facet, v.value),
                    });
                }
            }
        }

        const hasSelected = selectedPills.length > 0;

        this.container.innerHTML = /*html*/ `
            <div class="card">
                <div class="card-header">
                    <h5 class="card-title mb-0 fw-semibold">${this.escapeHTML(i18n.t('facetPanel.heading'))}</h5>
                    ${hasSelected ? /*html*/ `
                    <div class="d-flex flex-wrap gap-1 mt-2">
                        ${selectedPills.map((pill) => /*html*/ `
                                <span class="badge text-bg-primary d-inline-flex align-items-center gap-1 px-2 py-1">
                                <span>${this.escapeHTML(pill.facetLabel)}: ${pill.displayValue}</span>
                                <button type="button" class="btn-close btn-close-white ms-1" style="font-size: 0.5rem;"
                                    data-facet-key="${this.escapeAttr(pill.facetKey)}"
                                    data-facet-value="${this.escapeAttr(pill.rawValue)}"
                                    aria-label="${this.escapeAttr(i18n.t('facetPanel.removeLabel'))}"></button>
                            </span>
                        `).join('')}
                    </div>
                    ` : ''}
                </div>
                <div class="card-body">
                    ${facets.map((facet) => /*html*/ `
                        <div class="mb-3">
                            <h6 class="fw-semibold">${this.escapeHTML(facet.label)}</h6>
                            <div>
                                ${facet.values.map((v) => /*html*/ `
                                    <div>
                                        <a class="text-decoration-none${v.selected ? ' fw-bold' : ''}"
                                           href="javascript:void(0)"
                                           data-facet-key="${this.escapeAttr(facet.key)}"
                                           data-facet-value="${this.escapeAttr(String(v.value))}">
                                            <span>${this.renderFacetValue(facet, v.value)}</span>
                                            (<span>${v.count}</span>)
                                        </a>
                                    </div>
                                `).join('')}
                            </div>
                        </div>
                    `).join('')}
                    ${hasSelected ? `<button type="button" class="btn btn-sm btn-primary btn-clear-facets">${this.escapeHTML(i18n.t('facetPanel.clear'))}</button>` : ''}
                </div>
            </div>
        `;
    }

    private escapeHTML(str: string): string {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }

    private escapeAttr(str: string): string {
        return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    }

    private renderFacetValue(facet: FacetData, value: any): string {
        const format = facet.format || '{value}';
        return renderFormat(format, { value });
    }

}

customElements.define('eum-facet-panel', EumFacetPanel);